import { useCallback, useEffect, useRef, useState } from 'react'
import { useMapLayers } from './useMapLayers'
import type { LayerEventHandlers } from '../types'
import { MapSettings, MapStyle } from '@/components/OrderDispatch/DriverTracking/components/DriverMap/types'
import {
  DEFAULT_MAP_SETTINGS,
  MAP_STYLE_OPTIONS,
} from '@/components/OrderDispatch/DriverTracking/components/DriverMap/constants'
import { useUserSettings } from '@/hooks/settings'
import { useLocalStorage, useThemeContext } from '@gravitate-js/excalibrr'
import { MapboxCoords } from '@/components/OrderDispatch/DriverTracking/types'

export type MapboxMapHook = ReturnType<typeof useMapboxMap>
export function useMapboxMap(mapKey: string) {
  const mapSettingsKey = `${mapKey}::map-settings`

  const mapRef = useRef<mapboxgl.Map | null>(null)
  const { value: mapSettings, setValue: setMapSettings } = useLocalStorage<MapSettings>(
    mapSettingsKey,
    DEFAULT_MAP_SETTINGS
  )
  const [gravitateMapStyleURL, setGravitateMapStyleURL] = useState<string>(MAP_STYLE_OPTIONS.default.sd_light)
  const [isStyleLoaded, setIsStyleLoaded] = useState<boolean>(false)

  const mapLayersHook = useMapLayers(mapRef)
  const { updateSetting, getSetting } = useUserSettings()
  const { currentTheme } = useThemeContext()

  useEffect(() => {
    getSetting(mapSettingsKey).then((storedStyle: MapSettings) => {
      if (!storedStyle) return
      setMapSettings(storedStyle)
      handleUpdateMapStyleURL(storedStyle.style)
    })
  }, [])

  useEffect(() => {
    handleMapStyleChange(mapSettings?.style || 'default')
  }, [currentTheme])

  function useLayer(layerId: string, handlers: LayerEventHandlers) {
    useEffect(() => {
      mapLayersHook.registerLayer(layerId, handlers)
      return () => {
        mapLayersHook.unregisterLayer(layerId)
      }
    }, [layerId, handlers])
  }

  const flyToLocation = useCallback((coords: MapboxCoords, zoom = 15, speed = 1.2, curve = 1.5) => {
    if (!mapRef.current) return
    mapRef.current.flyTo({
      center: coords,
      zoom,
      speed,
      curve,
    })
  }, [])

  function handleUpdateMapSettings<K extends keyof MapSettings>(key: K, value: MapSettings[K]) {
    const newSettings = { ...(mapSettings || DEFAULT_MAP_SETTINGS), [key]: value }
    setMapSettings(newSettings)
    updateSetting(mapSettingsKey, newSettings)
  }

  function handleUpdateMapStyleURL(style: MapStyle) {
    if (style === 'satellite') {
      setGravitateMapStyleURL(MAP_STYLE_OPTIONS.satellite as string)
    } else {
      setGravitateMapStyleURL(MAP_STYLE_OPTIONS[style][currentTheme] as string)
    }
  }

  function handleMapStyleChange(style: MapStyle) {
    // If switching away from satellite, we need to reset the isStyleLoaded state
    // we can ignore satellite because it is not affected by light/dark mode
    if (style !== 'satellite' || mapSettings?.style !== 'satellite') {
      setIsStyleLoaded(false)
    }

    handleUpdateMapSettings('style', style)
    handleUpdateMapStyleURL(style)
  }

  const getMapLayerIds = useCallback((): string[] => {
    if (!mapRef.current) return []

    const style = mapRef.current.getStyle()
    return style.layers ? style.layers.map((layer) => layer.id) : []
  }, [])

  return {
    mapRef,
    gravitateMapStyleURL,
    isStyleLoaded,
    setIsStyleLoaded,
    useLayer,
    flyToLocation,
    getMapLayerIds,
    handleMapStyleChange,
    handleUpdateMapSettings,
    mapSettings,
    ...mapLayersHook,
  }
}
